import type { MouseEvent } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  ClipboardCheck,
  FileText,
  LayoutGrid,
  Link2,
  Stethoscope,
  User,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLinkTitle } from '../../hooks/chat/useChatQueries';
import { askBeforeLeaving, LEAVE_GUARD_SKIP } from '../../hooks/useLeaveGuard';
import type { LinkTitleOut } from '../../services/api';
import { useStore } from '../../store';

const KIND_ICON: Record<NonNullable<LinkTitleOut['kind']>, LucideIcon> = {
  patient: User,
  visit: Stethoscope,
  qa_visit: ClipboardCheck,
  document: FileText,
  intake: UserPlus,
  page: LayoutGrid,
};

/** A Roger address shown as the reader's own title: who, then which page. */
export function LinkChip({ url, isOwn }: { url: string; isOwn: boolean }) {
  const navigate = useNavigate();
  const chatLayout = useStore(state => state.chatLayout);
  const setChatSheetOpen = useStore(state => state.setChatSheetOpen);
  const { data, isPending } = useLinkTitle(url);

  const target = new URL(url);
  const sameHost = target.host === window.location.host;

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // A touch tap must not also select the bubble.
    event.stopPropagation();
    if (!sameHost) return;
    // Let the browser handle open-in-new-tab gestures.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    const go = () => {
      navigate(target.pathname + target.search + target.hash);
      if (chatLayout === 'sheet') setChatSheetOpen(false);
    };
    if (!askBeforeLeaving(go)) go();
  };

  const Icon = (data?.kind && KIND_ICON[data.kind]) || Link2;
  const pageColor = isOwn
    ? 'text-primary-foreground/75'
    : 'text-muted-foreground';
  const tint = isOwn ? 'bg-white/20' : 'bg-primary/10';
  const skeleton = isOwn ? 'bg-white/30' : 'bg-foreground/15';

  let label;
  if (isPending) {
    label = (
      <>
        <span
          className={`inline-block h-3 w-24 animate-pulse rounded-md ${skeleton}`}
          aria-hidden
        />
        <span className='sr-only'>Roger link, loading</span>
      </>
    );
  } else if (!data?.page) {
    label = <span className='truncate'>Roger link</span>;
  } else if (data.name) {
    label = (
      <>
        <span className='min-w-[3ch] shrink-[1000] truncate font-medium'>
          {data.name}
        </span>
        <span className={`max-w-[65%] shrink-0 truncate ${pageColor}`}>
          · {data.page}
        </span>
      </>
    );
  } else {
    label = <span className='truncate'>{data.page}</span>;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={url}
            onClick={onClick}
            {...LEAVE_GUARD_SKIP}
            {...(sameHost
              ? {}
              : { target: '_blank', rel: 'noopener noreferrer' })}
            className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded px-1 align-bottom hover:underline focus-visible:underline ${tint}`}
          >
            <Icon className='h-3.5 w-3.5 shrink-0' aria-hidden />
            {label}
          </a>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs break-all text-xs'>
          {url}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
